import { Box, Typography } from "@mui/material";
import React from "react";
import { Link } from "react-router-dom";
import SEO, { SITE_URL } from "../components/SEO";
import { SX } from "../sx";
import { C, F, R, S, maxW, trans } from "../tokens";

const BIENFAITS = [
  {
    icon: "💧",
    title: "Réduction des œdèmes",
    desc: "Jambes lourdes, chevilles gonflées, rétention d'eau : la lymphe repart, les tissus se libèrent.",
  },
  {
    icon: "🛡️",
    title: "Immunité renforcée",
    desc: "Une meilleure circulation lymphatique aide le corps à filtrer les agents pathogènes et à réduire les infections récurrentes.",
  },
  {
    icon: "🌿",
    title: "Détente profonde",
    desc: "La technique active le système nerveux parasympathique, induisant un état de calme et de récupération.",
  },
  {
    icon: "⚡",
    title: "Regain d'énergie",
    desc: "En éliminant les déchets métaboliques, le drainage favorise une meilleure oxygénation des tissus et combat la fatigue chronique.",
  },
  {
    icon: "✨",
    title: "Peau et cicatrisation",
    desc: "Meilleure élimination des toxines, amélioration de l'éclat cutané et soutien à la récupération post-opératoire.",
  },
  {
    icon: "🧘",
    title: "Gestion du stress",
    desc: "Idéal en période de surcharge émotionnelle ou physique, le drainage agit comme un véritable « reset » du corps.",
  },
];

const INDICATIONS = [
  "Jambes lourdes, varices",
  "Rétention d'eau",
  "Grossesse (2ème & 3ème trimestre)",
  "Fatigue chronique",
  "Convalescence / post-opératoire",
  "Sinusites et otites à répétition",
  "Fibromyalgie",
  "Stress, anxiété",
  "Sportifs en récupération",
  "Lymphœdème post-cancer",
];

const STEPS = [
  {
    num: "01",
    title: "Entretien & anamnèse",
    desc: "Échange sur vos symptômes, antécédents et objectifs pour adapter la séance à votre situation spécifique.",
  },
  {
    num: "02",
    title: "Bilan ostéopathique ciblé",
    desc: "Identification des zones de tension, d'œdème ou de ralentissement circulatoire pour orienter le protocole.",
  },
  {
    num: "03",
    title: "Drainage manuel",
    desc: "Manœuvres lentes et très légères (moins de 30 g de pression) suivant les trajets lymphatiques  totalement indolores et profondément relaxantes.",
  },
  {
    num: "04",
    title: "Conseils personnalisés",
    desc: "Hydratation, exercices respiratoires, auto-drainage : des gestes simples pour prolonger les effets de la séance au quotidien.",
  },
];

const Drainage = () => (
  <>
    <SEO
      title="Drainage lymphatique manuel  Méthode Vodder  Balma"
      description="Drainage lymphatique manuel méthode Vodder avec Maxime Nicolaieff, ostéopathe D.O. à Balma. Jambes lourdes, œdèmes, fatigue, post-opératoire. Séance 100 €."
      path="/pathologies/drainage-lymphatique"
      schema={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Accueil",
            item: `${SITE_URL}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Pathologies",
            item: `${SITE_URL}/pathologies`,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "Drainage lymphatique",
            item: `${SITE_URL}/pathologies/drainage-lymphatique`,
          },
        ],
      }}
    />

    {/* ── Hero ──────────────────────────────── */}
    <Box component="section" sx={SX.pageHeroSection}>
      <Box sx={SX.pageHeroInner}>
        <Box component="span" sx={SX.sectionLabel}>
          Soin complémentaire
        </Box>
        <Typography variant="h1" component="h1" sx={SX.displayTitle}>
          Le drainage <em>lymphatique manuel.</em>
        </Typography>
        <Typography sx={SX.pageHeroSub}>
          Une technique douce et profondément réparatrice, intégrée à votre
          suivi ostéopathique pour libérer votre corps et renforcer vos défenses
          naturelles.
        </Typography>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "baseline",
            gap: ".75rem",
            mt: "2rem",
            pt: "2rem",
            borderTop: `1px solid ${C.border}`,
          }}
        >
          <Typography
            sx={{
              fontFamily: F.display,
              fontSize: "3.5rem",
              fontWeight: 300,
              color: C.text,
              lineHeight: 1,
            }}
          >
            100
            <Box
              component="span"
              sx={{ fontSize: "2rem", verticalAlign: "middle", ml: ".25rem" }}
            >
              €
            </Box>
          </Typography>
          <Typography
            sx={{ fontSize: ".8rem", color: C.textMuted, lineHeight: 1.4 }}
          >
            la séance sur rendez-vous
          </Typography>
        </Box>
      </Box>
    </Box>

    {/* ── Pourquoi le système lymphatique ───── */}
    <Box component="section" sx={{ py: { xs: "3rem", md: "5.5rem" } }}>
      <Box
        sx={{
          maxWidth: maxW,
          mx: "auto",
          px: { xs: "1rem", md: "2.5rem" },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: { xs: "2rem", md: "5rem" },
          alignItems: "start",
        }}
      >
        <Box>
          <Box component="span" sx={SX.sectionLabel}>
            Comprendre
          </Box>
          <Typography
            variant="h2"
            component="h2"
            sx={{ ...SX.displayTitle, mb: "1.25rem" }}
          >
            Pourquoi le système lymphatique est-il si <em>important ?</em>
          </Typography>
          <Typography
            sx={{
              fontSize: ".75rem",
              color: C.accent,
              fontWeight: 600,
              letterSpacing: "1px",
              textTransform: "uppercase",
            }}
          >
            Le réseau silencieux de votre immunité
          </Typography>
        </Box>
        <Box>
          <Typography
            sx={{
              fontSize: ".9rem",
              color: C.textMuted,
              lineHeight: 1.85,
              mb: "1.25rem",
            }}
          >
            Le système lymphatique est un réseau de vaisseaux et de ganglions
            qui collecte les déchets, les toxines et les cellules immunitaires
            dans tout votre organisme. Contrairement au sang, la lymphe ne
            dispose d'aucune pompe : elle circule grâce aux mouvements du corps
            et à la respiration.
          </Typography>
          <Typography
            sx={{ fontSize: ".9rem", color: C.textMuted, lineHeight: 1.85 }}
          >
            Lorsque sa circulation ralentit, les tissus se congèrent, les
            défenses immunitaires s'affaiblissent et la fatigue s'installe. Le
            drainage lymphatique manuel consiste à stimuler délicatement ces
            circulations pour relancer le flux, éliminer les surplus liquidiens
            et soutenir l'immunité le tout avec des pressions très légères,
            presque imperceptibles.
          </Typography>
        </Box>
      </Box>
    </Box>

    {/* ── Bienfaits ─────────────────────────── */}
    <Box
      component="section"
      sx={{ py: { xs: "3rem", md: "5.5rem" }, background: C.bgAlt }}
    >
      <Box
        sx={{ maxWidth: maxW, mx: "auto", px: { xs: "1rem", md: "2.5rem" } }}
      >
        <Box component="span" sx={SX.sectionLabel}>
          Bénéfices
        </Box>
        <Typography
          variant="h2"
          component="h2"
          sx={{ ...SX.displayTitle, mb: "2.5rem" }}
        >
          Les bienfaits <em>de la séance.</em>
        </Typography>
        <Typography
          sx={{
            fontSize: ".78rem",
            color: C.textMuted,
            mb: "2rem",
            fontStyle: "italic",
          }}
        >
          Ce que vous pouvez ressentir dès les premières séances
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(3, 1fr)",
            },
            gap: "1.25rem",
          }}
        >
          {BIENFAITS.map((b) => (
            <Box
              key={b.title}
              sx={{
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: R.lg,
                padding: "1.5rem",
                transition: `box-shadow ${trans}`,
                "&:hover": { boxShadow: S.md },
              }}
            >
              <Box
                component="span"
                sx={{ fontSize: "1.5rem", display: "block", mb: ".75rem" }}
              >
                {b.icon}
              </Box>
              <Typography
                sx={{
                  fontSize: ".9rem",
                  fontWeight: 600,
                  color: C.text,
                  mb: ".4rem",
                }}
              >
                {b.title}
              </Typography>
              <Typography
                sx={{ fontSize: ".8rem", color: C.textMuted, lineHeight: 1.65 }}
              >
                {b.desc}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>

    {/* ── Indications ───────────────────────── */}
    <Box component="section" sx={{ py: { xs: "3rem", md: "5.5rem" } }}>
      <Box
        sx={{ maxWidth: maxW, mx: "auto", px: { xs: "1rem", md: "2.5rem" } }}
      >
        <Box component="span" sx={SX.sectionLabel}>
          Indications
        </Box>
        <Typography
          variant="h2"
          component="h2"
          sx={{ ...SX.displayTitle, mb: ".75rem" }}
        >
          Pour qui <em>est-ce indiqué ?</em>
        </Typography>
        <Typography sx={{ fontSize: ".82rem", color: C.textMuted, mb: "2rem" }}>
          Situations fréquentes où le drainage lymphatique peut aider
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(3, 1fr)",
            },
            gap: ".6rem",
            mb: "2.5rem",
          }}
        >
          {INDICATIONS.map((ind) => (
            <Box
              key={ind}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: ".75rem",
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: R.base,
                px: "1rem",
                py: ".7rem",
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  background: C.primary,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: ".65rem",
                  flexShrink: 0,
                }}
              >
                ✓
              </Box>
              <Typography sx={{ fontSize: ".82rem", color: C.text }}>
                {ind}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Contre-indications */}
        <Box
          sx={{
            display: "flex",
            gap: ".75rem",
            alignItems: "flex-start",
            background: "#edf5f1",
            border: `1px solid #b8d9c8`,
            borderRadius: R.base,
            px: "1.25rem",
            py: "1rem",
            fontSize: ".82rem",
            color: C.textMuted,
            lineHeight: 1.7,
          }}
        >
          <Box
            component="span"
            sx={{ fontSize: "1rem", flexShrink: 0, mt: ".05rem" }}
          >
            ℹ️
          </Box>
          <Box>
            Le drainage lymphatique est contre-indiqué en cas de thrombose
            veineuse aiguë, d'infection en phase active, de cancer évolutif non
            traité ou d'insuffisance cardiaque décompensée.{" "}
            <strong>
              Un bilan ostéopathique préalable permet de s'assurer que la
              technique est adaptée à votre situation.
            </strong>
          </Box>
        </Box>
      </Box>
    </Box>

    {/* ── Déroulement ───────────────────────── */}
    <Box
      component="section"
      sx={{ py: { xs: "3rem", md: "5.5rem" }, background: C.bgAlt }}
    >
      <Box
        sx={{
          maxWidth: maxW,
          mx: "auto",
          px: { xs: "1rem", md: "2.5rem" },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "360px 1fr" },
          gap: { xs: "3rem", md: "5rem" },
          alignItems: "start",
        }}
      >
        <Box>
          <Box component="span" sx={SX.sectionLabel}>
            Déroulement
          </Box>
          <Typography
            variant="h2"
            component="h2"
            sx={{ ...SX.displayTitle, mb: "1rem" }}
          >
            Comment se déroule <em>la séance ?</em>
          </Typography>
          <Typography
            sx={{ fontSize: ".82rem", color: C.textMuted, fontStyle: "italic" }}
          >
            Un protocole doux, adapté à chaque patient
          </Typography>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {STEPS.map((s, i) => (
            <Box
              key={s.num}
              sx={{
                display: "grid",
                gridTemplateColumns: "48px 1fr",
                gap: "1.25rem",
                alignItems: "start",
                py: "1.5rem",
                borderBottom:
                  i < STEPS.length - 1 ? `1px solid ${C.border}` : "none",
              }}
            >
              <Box
                component="span"
                sx={{
                  fontFamily: F.display,
                  fontSize: "1.8rem",
                  fontWeight: 300,
                  color: C.accent,
                  lineHeight: 1,
                  pt: ".1rem",
                }}
              >
                {s.num}
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: ".92rem",
                    fontWeight: 600,
                    color: C.text,
                    mb: ".3rem",
                  }}
                >
                  {s.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: ".82rem",
                    color: C.textMuted,
                    lineHeight: 1.7,
                  }}
                >
                  {s.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>

    {/* ── CTA ───────────────────────────────── */}
    <Box
      component="section"
      sx={{ background: C.dark, py: { xs: "3.5rem", md: "6rem" } }}
    >
      <Box
        sx={{
          maxWidth: maxW,
          mx: "auto",
          px: { xs: "1rem", md: "2.5rem" },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
          gap: "3rem",
          alignItems: "center",
        }}
      >
        <Box>
          <Box component="span" sx={SX.sectionLabelWhite}>
            Prendre rendez-vous
          </Box>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontFamily: F.display,
              fontSize: "clamp(2rem, 3.5vw, 3rem)",
              fontWeight: 300,
              color: "#fff",
              lineHeight: 1.2,
              mb: ".75rem",
              "& em": { fontStyle: "italic", color: "rgba(255,255,255,.6)" },
            }}
          >
            Séance de drainage,
            <br />
            <em>dès cette semaine.</em>
          </Typography>
          <Typography
            sx={{
              fontSize: ".85rem",
              color: "rgba(255,255,255,.55)",
              maxWidth: "420px",
              lineHeight: 1.75,
              mb: "1.5rem",
            }}
          >
            Séance de drainage lymphatique{" "}
            <strong style={{ color: "rgba(255,255,255,.8)" }}>100 €</strong>{" "}
            environ 60. Formé spécifiquement à la méthode Vodder, en complément
            du cursus ostéopathique (5 ans).
          </Typography>
          <Box sx={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            <Box
              component="a"
              href="https://www.doctolib.fr/osteopathe/balma/maxime-nicolaieff"
              target="_blank"
              rel="noreferrer"
              sx={SX.btnAccent}
            >
              Réserver une séance
            </Box>
            <Box component="a" href="tel:0695788983" sx={SX.btnOutlineWhite}>
              06 95 78 89 83
            </Box>
          </Box>
        </Box>
        <Box sx={{ textAlign: { xs: "left", md: "center" } }}>
          <Typography
            sx={{
              fontFamily: F.display,
              fontSize: "5rem",
              fontWeight: 300,
              color: "#fff",
              lineHeight: 1,
            }}
          >
            100
            <Box
              component="sup"
              sx={{ fontSize: "2rem", fontWeight: 400, verticalAlign: "super" }}
            >
              €
            </Box>
          </Typography>
          <Typography
            sx={{
              fontSize: ".72rem",
              color: "rgba(255,255,255,.4)",
              mt: ".6rem",
              letterSpacing: "1px",
              textTransform: "uppercase",
            }}
          >
            la séance
            <br />
            méthode Vodder
          </Typography>
        </Box>
      </Box>
    </Box>

    {/* ── Liens internes ────────────────────── */}
    <Box
      sx={{
        maxWidth: maxW,
        mx: "auto",
        px: { xs: "1rem", md: "2.5rem" },
        py: { xs: "2.5rem", md: "3rem" },
        display: "flex",
        gap: "1rem",
        flexWrap: "wrap",
      }}
    >
      <Box
        component={Link}
        to="/tarifs-et-remboursements"
        sx={{
          fontSize: ".85rem",
          color: C.primary,
          textDecoration: "none",
          fontWeight: 500,
          "&:hover": { textDecoration: "underline" },
        }}
      >
        Consulter les tarifs →
      </Box>
      <Box
        component={Link}
        to="/pathologies"
        sx={{
          fontSize: ".85rem",
          color: C.primary,
          textDecoration: "none",
          fontWeight: 500,
          "&:hover": { textDecoration: "underline" },
        }}
      >
        Toutes les pathologies →
      </Box>
    </Box>
  </>
);

export default Drainage;
